// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 88ce2865-f5e0-42bf-9620-43e32fd99cc8

// ---------------------------------------------------------------------------
// Tenant-scoped lookup of a fact sheet by name.
//
// Integrations name their endpoints in free text — `to: "Epic"`, `via:
// "Rhapsody"` — so every map screen has to resolve those strings back to a fact
// sheet by name. Inside a client workspace that is unambiguous.
//
// In the master workspace it is not. `store.listFactSheets('optimum', …)`
// returns EVERY tenant's rows, so a bare name key silently merges clients: two
// health systems that both run Epic collapse into one entry, and whichever row
// happened to land in it decides where every arrow points. The screens then
// render that as a clickable link — one client's application opening another
// client's fact sheet. Four screens each hand-rolled the same two lines, so the
// same defect had to be found four times; it lives here now so there is one
// place to get it right.
//
// A name only means anything inside the tenant that wrote it, so the key is the
// pair. Resolution always starts from the fact sheet that OWNS the endpoint and
// never leaves its tenant. In a single-client workspace every row carries the
// same tenant, so this is exactly the previous behaviour.
// ---------------------------------------------------------------------------

export const normName = (s) => String(s == null ? '' : s).trim().toLowerCase();

// NUL cannot appear in a tenant id or a fact-sheet name, so no application can
// be named in a way that forges a key into another tenant.
const SEP = '\u0000';

export function nameKey(tenantId, name) {
  const n = normName(name);
  return n ? `${tenantId == null ? '' : String(tenantId)}${SEP}${n}` : '';
}

// `firstWins` picks the collision policy for two rows that share a name WITHIN
// one tenant — a real single-client ambiguity, and a different question from the
// cross-tenant one this module exists to close. Each screen keeps whichever
// policy it already had.
export function buildNameIndex(rows, { firstWins = false } = {}) {
  const index = new Map();
  (rows || []).forEach((fs) => {
    if (!fs) return;
    const k = nameKey(fs.tenantId, fs.name);
    if (!k) return;
    if (firstWins && index.has(k)) return;
    index.set(k, fs);
  });
  return index;
}

// `owner` is the fact sheet the endpoint was written on — never the workspace
// being viewed. Passing the workspace would reintroduce the bug: in `optimum`
// there is no such tenant, and every lookup would miss.
export function lookupName(index, owner, name) {
  if (!index || !owner) return null;
  const k = nameKey(owner.tenantId, name);
  return k ? index.get(k) || null : null;
}
